/**
 * Produces a human-readable hint describing what a metadata `regex` accepts,
 * so validation errors can tell the user what a valid value looks like
 * instead of a bare "Please enter a valid <field>".
 *
 * Backend metadata SHOULD provide `regex_message`; this is the fallback used
 * when it does not. It intentionally only recognises the common patterns used
 * by the metadata API (numeric ranges, decimals, fixed alphanumeric masks) and
 * returns an empty string for anything it cannot confidently describe, so the
 * caller can fall back to a generic message.
 */

/** Escapes handled internally; input is a raw regex source string (no delimiters). */
export function describeRegexConstraint(regex: string): string {
  if (!regex) return ''

  const clean = regex.replace(/^\^/, '').replace(/\$+$/, '').trim()

  // --- Email address ---
  // e.g. ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$
  if (/@.*\\?\.\[a-zA-Z\]/.test(clean) || /@\[a-zA-Z0-9/.test(clean)) {
    return 'a valid email address (e.g. name@example.com)'
  }

  // --- 4-digit year, e.g. ^(19|20)\d{2}$ ---
  if (/^\(19\|20\)\\d\{2\}$/.test(clean) || /\(19\|20\)\\d\{2\}/.test(clean)) {
    return 'a 4-digit year between 1900 and 2099 (e.g. 2024)'
  }

  // --- 2-digit month, e.g. ^(0[1-9]|1[0-2])$ ---
  if (/0\[1-9\]\|1\[0-2\]/.test(clean)) {
    return 'a 2-digit month from 01 to 12 (e.g. 06)'
  }

  // --- "No caret / control characters" text mask, e.g. ^[^\^\x00-\x1F]*$ ---
  // Used by most free-text fields — everything except the ^ symbol and control chars.
  if (
    /\[\^\\\^\\x00-\\x1F\]/.test(clean) ||
    /\[\^\^\\x00-\\x1F\]/.test(clean)
  ) {
    return 'text without the ^ symbol or control characters'
  }

  // --- Any-character length cap, e.g. ^[\w\W]{1,200}$ ---
  const anyCharLen = /\[\\w\\W\]\{1,(\d+)\}/.exec(clean)
  if (anyCharLen) {
    return `up to ${anyCharLen[1]} characters`
  }

  // --- Percentage / rate: 0–100 with optional decimals ---
  // e.g. ^(100(\.00?)?|[0-9]{1,2}(\.[0-9]{1,2})?)$
  if (/100.*\|.*\[0-9\]\{1,2\}/.test(clean) && clean.includes('.')) {
    const decMatch = /\\?\.\[0-9\]\{1,(\d+)\}/.exec(clean)
    const decimals = decMatch ? Number(decMatch[1]) : undefined
    return decimals
      ? `a number from 0 to 100 with up to ${decimals} decimal place${decimals > 1 ? 's' : ''} (e.g. 12.50)`
      : 'a number from 0 to 100'
  }

  // --- Generic decimal number with limited decimals ---
  // e.g. ^[0-9]+(\.[0-9]{1,2})?$ or ^[0-9]*\.?[0-9]{1,2}$
  const decOnly = /\[0-9\][+*]?.*\\?\.\[0-9\]\{1,(\d+)\}/.exec(clean)
  if (decOnly && !/[A-Za-z]/.test(clean.replace(/\\d|\\\./g, ''))) {
    return `a number with up to ${decOnly[1]} decimal place${Number(decOnly[1]) > 1 ? 's' : ''} (e.g. 1234.56)`
  }

  // --- Whole number only ---
  if (/^\[0-9\]\+?$/.test(clean) || /^\\d\+?$/.test(clean)) {
    return 'a whole number (digits only)'
  }

  // --- Fixed alphanumeric mask, e.g. [A-Z]{4}[0-9]{4} -> "AAAA0000" ---
  const maskTokens =
    /^(?:\[[^\]]+\]\{\d+\}|\[[^\]]+\]|[A-Za-z0-9]\{\d+\}|[A-Za-z0-9])+$/
  if (maskTokens.test(clean)) {
    const hint = buildMaskHint(clean)
    if (hint) return `the format ${hint}`
  }

  return ''
}

function buildMaskHint(clean: string): string {
  let hint = ''
  const tokenRe =
    /\[([^\]]+)\]\{(\d+)\}|\[([^\]]+)\]|([A-Za-z0-9])\{(\d+)\}|([A-Za-z0-9])/g
  let match: RegExpExecArray | null
  const sample = (cls: string): string =>
    cls.includes('A-Z')
      ? 'A'
      : cls.includes('a-z')
        ? 'a'
        : cls.includes('0-9')
          ? '0'
          : cls[0]
  while ((match = tokenRe.exec(clean)) !== null) {
    if (match[1] && match[2]) hint += sample(match[1]).repeat(Number(match[2]))
    else if (match[3]) hint += sample(match[3])
    else if (match[4] && match[5]) hint += match[4].repeat(Number(match[5]))
    else if (match[6]) hint += match[6]
  }
  return hint
}

/**
 * Builds the final regex-validation error message for a field.
 * Priority: explicit backend `regex_message` -> derived hint -> generic.
 */
export function buildRegexErrorMessage(
  label: string,
  regex: string | undefined,
  regexMessage?: string
): string {
  // Backend-provided message always wins.
  if (regexMessage) return regexMessage
  const hint = regex ? describeRegexConstraint(regex) : ''
  // State what's wrong AND what a valid value looks like, so the user knows
  // exactly what to enter (e.g. "Email is invalid. Please enter a valid email
  // address (e.g. name@example.com)").
  return hint
    ? `${label} is invalid. Please enter ${hint}`
    : `${label} is invalid. Please enter a valid value`
}
